import { useEffect, useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { Textarea } from "../design/textarea";
import { PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useHoliday } from "../contexts/HolidayContext";
import { Table, TableBody, TableCell, TableRow } from "../design/table";
import { Holiday } from "../models/Holiday";
import {
  HolidayAssignmentMode,
  holidayAssignmentModes,
} from "../models/HolidayAssignmentMode";
import { Select } from "../design/select";

interface Props {
  holidayId?: Holiday["_id"];
  holiday?: Holiday;
  children?: React.ReactNode;
  className?: string;
}

const HolidayButton: React.FC<Props> = ({
  holiday,
  holidayId,
  children,
  className,
}) => {
  const { createHoliday, updateHoliday } = useHoliday();

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assignmentMode, setAssignmentMode] =
    useState<HolidayAssignmentMode>("every");
  const [startOffsetMinutes, setStartOffsetMinutes] = useState(540); // 9 AM
  const [endOffsetMinutes, setEndOffsetMinutes] = useState(1080); // 6 PM
  const [dates, setDates] = useState<string[]>([]);
  const [newDate, setNewDate] = useState<string>("");
  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (holiday && isOpen) {
      setName(holiday.name || "");
      setDescription(holiday.description || "");
      setDates(holiday.dates || []);
    }
  }, [holiday, isOpen]);

  const validateForm = () => {
    const newErrors: { name?: string } = {};
    if (!name.trim()) newErrors.name = "Name is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddDate = () => {
    const trimmed = newDate.trim();
    if (!trimmed || !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return;
    if (!dates.includes(trimmed)) {
      setDates((prev) => [...prev, trimmed]);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      if (holiday) {
        await updateHoliday(holiday._id, {
          ...holiday,
          name,
          description,
          dates,
        });
      } else {
        await createHoliday({
          name,
          description,
          dates,
          assignmentMode,
          startOffsetMinutes,
          endOffsetMinutes,
        });
      }
      setIsOpen(false);
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, name: err.message }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            {!holiday && <PlusIcon className="w-4 h-4" />}
            <span>{holiday ? "Edit Holiday" : "Create Holiday"}</span>
          </div>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>{holiday ? "Edit Holiday" : "Create Holiday"}</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  value={name}
                  placeholder="Name"
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  placeholder="Description"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={new Date(startOffsetMinutes * 60 * 1000)
                      .toISOString()
                      .substring(11, 16)}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value
                        .split(":")
                        .map(Number);
                      setStartOffsetMinutes(hours * 60 + minutes);
                    }}
                  />
                </Field>
                <Field>
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={new Date(endOffsetMinutes * 60 * 1000)
                      .toISOString()
                      .substring(11, 16)}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value
                        .split(":")
                        .map(Number);
                      setEndOffsetMinutes(hours * 60 + minutes);
                    }}
                  />
                </Field>
              </div>
              <Field>
                <Label>Assignment Mode</Label>
                <Select
                  value={assignmentMode}
                  onChange={(e) =>
                    setAssignmentMode(e.target.value as HolidayAssignmentMode)
                  }
                >
                  {Object.entries(holidayAssignmentModes).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ),
                  )}
                </Select>
              </Field>
            </FieldGroup>

            <FieldGroup>
              <Field>
                <Label>Add Date (YYYY-MM-DD)</Label>
                <div className="flex gap-2">
                  <Input
                    value={newDate}
                    placeholder="e.g. 2025-05-11"
                    onChange={(e) => setNewDate(e.target.value)}
                  />
                  <Button type="button" onClick={handleAddDate}>
                    Add
                  </Button>
                </div>
              </Field>
            </FieldGroup>

            <Field>
              <Label>Dates</Label>
              <Table>
                <TableBody>
                  {dates.sort().map((date, index) => (
                    <TableRow key={index}>
                      <TableCell>{date}</TableCell>
                      <TableCell className="justify-end">
                        <Button
                          color="red"
                          onClick={() =>
                            setDates((prev) =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                        >
                          <TrashIcon className="w-5 h-5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Field>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HolidayButton;
